import { t } from '@lingui/core/macro';
import { type BookingRecordDetailsCustomer, BookingUpdateOfferStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiInfoBanner } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useNavigate } from 'react-router-dom';

interface Props {
  booking: BookingRecordDetailsCustomer;
}

export const BookingUpdateOfferSection = ({ booking }: Props) => {
  const navigate = useNavigate();

  const { data: offer, error: offerError } = noddiAsync.useGet({
    type: URLKeys.getBookingActiveBookingUpdateOffer,
    input: { slug: booking.slug }
  });

  if (!offer || offerError) {
    return null;
  }

  if (offer.booking.slug !== booking.slug && offer.bookingId !== booking.id) {
    return null;
  }

  if (offer.status === BookingUpdateOfferStatus.ACCEPTED || offer.status === BookingUpdateOfferStatus.DECLINED) {
    return null;
  }

  if (offer.expired) {
    return (
      <NoddiInfoBanner
        variant='info'
        texts={[
          { value: t`We sent you a request to change your booking, but the deadline has passed. No action needed.` }
        ]}
      />
    );
  }

  return (
    <button
      type='button'
      className='w-full cursor-pointer rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-left transition-colors hover:bg-yellow-100'
      // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
      onClick={() => navigate(customerRoutes.bookingUpdateOffer.getPath({ slug: booking.slug }))}
    >
      <div className='flex items-center gap-3'>
        <NoddiIcon name='CalendarClock' size='small' className='shrink-0 text-yellow-700' />
        <div className='min-w-0 flex-1 text-sm text-yellow-900'>
          <p className='font-medium'>{t`Can you be flexible with your time?`}</p>
          <p className='mt-0.5 text-yellow-800'>{t`We'd appreciate your help — tap to see your options.`}</p>
        </div>
        <NoddiIcon name='ChevronRight' size='small' className='shrink-0 text-yellow-600' />
      </div>
    </button>
  );
};
